/**
 * SucursalPicker — al entrar, la regente elige en qué farmacia está (2026-09-30).
 * Lista del API de Hipermaxi (GET /api/sucursales), agrupada por región, con buscador.
 */
import { useEffect, useMemo, useState } from 'react'
import type { SucursalElegida } from '../hooks/useSucursal'

interface Sucursal {
  id_und_neg: number
  nombre: string
  region: string
}

interface Props {
  onElegir: (s: SucursalElegida) => void
}

export function SucursalPicker({ onElegir }: Props) {
  const [lista, setLista] = useState<Sucursal[] | null>(null)
  const [error, setError] = useState(false)
  const [q, setQ] = useState('')

  useEffect(() => {
    fetch('/api/sucursales')
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status))
        return r.json() as Promise<Sucursal[]>
      })
      .then(setLista)
      .catch(() => setError(true))
  }, [])

  const porRegion = useMemo(() => {
    const t = q.trim().toLowerCase()
    const filtradas = (lista ?? []).filter(
      (s) => !t || s.nombre.toLowerCase().includes(t) || s.region.toLowerCase().includes(t),
    )
    const grupos = new Map<string, Sucursal[]>()
    for (const s of filtradas) grupos.set(s.region, [...(grupos.get(s.region) ?? []), s])
    return [...grupos.entries()]
  }, [lista, q])

  return (
    <div
      className="min-h-screen flex justify-center px-4 py-8"
      style={{ background: 'var(--color-bg, #faf7f5)' }}
    >
      <div className="w-full max-w-xl grid gap-4 content-start">
        <div className="grid gap-1">
          <h1 className="text-[22px] font-semibold" style={{ color: 'var(--color-text)' }}>
            ¿En qué sucursal estás?
          </h1>
          <p className="text-[14px]" style={{ color: 'var(--color-text-muted)' }}>
            El asistente te muestra el stock y los equivalentes que hay en tu farmacia.
          </p>
        </div>

        <input
          type="search"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar sucursal (ej. Norte, Equipetrol, Prado)"
          aria-label="Buscar sucursal"
          className="w-full rounded-xl px-4 py-3 text-[15px] outline-none"
          style={{
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
          }}
        />

        {error && (
          <p className="text-[14px]" style={{ color: 'var(--color-warn, #b91c1c)' }}>
            No se pudo cargar la lista de sucursales. Revisá la conexión y recargá la página.
          </p>
        )}
        {!lista && !error && (
          <p className="text-[14px]" style={{ color: 'var(--color-text-muted)' }}>
            Cargando sucursales…
          </p>
        )}

        {porRegion.map(([region, sucs]) => (
          <section key={region} className="grid gap-2">
            <h2
              className="text-[12px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {region}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {sucs.map((s) => (
                <button
                  key={s.id_und_neg}
                  type="button"
                  onClick={() => onElegir({ id_und_neg: s.id_und_neg, nombre: s.nombre })}
                  className="text-left rounded-xl px-3 py-3 min-h-[48px] text-[14px] font-medium transition-colors"
                  style={{
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text)',
                  }}
                >
                  {s.nombre}
                </button>
              ))}
            </div>
          </section>
        ))}
        {lista && porRegion.length === 0 && (
          <p className="text-[14px]" style={{ color: 'var(--color-text-muted)' }}>
            Ninguna sucursal coincide con "{q}".
          </p>
        )}
      </div>
    </div>
  )
}
